import React, { useCallback, useMemo, useState } from "react";
import { Button, Group, Modal, Select, Stack, Tabs, Text } from "@mantine/core";
import { useDebouncedValue, useMediaQuery } from "@mantine/hooks";
import { useForm, yupResolver } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { EmployeeShiftRecord } from "../services/model/EmployeeShiftRecord";
import { ShiftConfig } from "../services/model/ShiftLocation";
import { ClockType, ShiftClockWarning } from "../services/model/ShiftClock";
import ClockOverrideSchema from "../services/validator/ClockOverrideSchema";
import {
  useCreateBulkShiftClockApiService,
  useCheckShiftClockApiService,
} from "../services/api/ShiftClockApiService";
import { useSiteEmployeesApiService } from "../services/api/SiteEmployeesApiService";
import {
  buildIsoTimestamp,
  formatTimeHHMM,
  getActiveShiftConfig,
} from "../utils/attendanceUtils";
import OverrideForm from "./OverrideForm";
import EmployeeCheckboxList, { ClockCandidate } from "./EmployeeCheckboxList";

interface RosterOption {
  id: string;
  name: string;
}

interface BatchClockFormProps {
  employees: EmployeeShiftRecord[];
  shiftConfigs: ShiftConfig[];
  locationId: string;
  /** Rosters (shift locations) in the same site, for the roster switcher. */
  rosters: RosterOption[];
  /** Switch which roster's employees are being clocked. */
  onRosterChange: (locationId: string) => void;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormValues {
  clock_type: string;
  timestamp: string;
  reason: string;
  no_show_reason: string;
  no_show_reason_other: string;
}

const TAB_KEYS = {
  CLOCK_IN: "clock_in",
  CLOCK_OUT: "clock_out",
};

const toCandidate = (r: EmployeeShiftRecord): ClockCandidate => ({
  employee_id: r.employee_id,
  employee_name: r.employee_name,
  is_on_site: Boolean(r.clock_in_time) && !r.clock_out_time,
});

const BatchClockForm: React.FC<BatchClockFormProps> = ({
  employees,
  shiftConfigs,
  locationId,
  rosters,
  onRosterChange,
  onSuccess,
  onCancel,
}) => {
  const isPhone = useMediaQuery("(max-width: 639px)");
  // Default to the current/upcoming shift
  const defaultShiftId = useMemo(
    () => getActiveShiftConfig(shiftConfigs) || shiftConfigs[0]?.id || "",
    [shiftConfigs]
  );

  const [selectedShiftId, setSelectedShiftId] =
    useState<string>(defaultShiftId);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
  const [warnings, setWarnings] = useState<ShiftClockWarning[]>([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(employeeSearch, 300);

  const isClockOut = activeTabIndex === 1;

  // Company-wide employee search — only on the Clock In tab, so an admin can
  // clock in an unscheduled / cross-site officer who isn't on this roster.
  // Reuses the replacement picker endpoint (employer-wide, on-site flagged).
  const { data: companyEmployees, isFetching: isSearchingCompany } =
    useSiteEmployeesApiService({
      locationId,
      search: debouncedSearch || undefined,
      enabled: !isClockOut && debouncedSearch.trim().length >= 2,
    });

  // Clock Out: everyone currently on site at this roster, regardless of which
  // shift their clock-in was attributed to. This is what lets a day-attributed
  // or unscheduled officer be clocked out from the (now active) night shift.
  const onSiteCandidates = useMemo<ClockCandidate[]>(() => {
    const byId = new Map<number, ClockCandidate>();
    employees.forEach((e) => {
      if (e.clock_in_time && !e.clock_out_time && !byId.has(e.employee_id)) {
        byId.set(e.employee_id, toCandidate(e));
      }
    });
    return Array.from(byId.values());
  }, [employees]);

  // Clock In default: the roster's people for the selected shift (the routine
  // "clock in tonight's crew" list).
  const scheduledCandidates = useMemo<ClockCandidate[]>(() => {
    const base = selectedShiftId
      ? employees.filter((e) => e.shift_config_id === selectedShiftId)
      : employees;
    return base.map(toCandidate);
  }, [employees, selectedShiftId]);

  // Clock In: scheduled crew + anyone surfaced by the company-wide search.
  const clockInCandidates = useMemo<ClockCandidate[]>(() => {
    const byId = new Map<number, ClockCandidate>();
    scheduledCandidates.forEach((c) => byId.set(c.employee_id, c));
    (companyEmployees?.results || []).forEach((e) => {
      if (!byId.has(e.employee_id)) {
        byId.set(e.employee_id, {
          employee_id: e.employee_id,
          employee_name: e.employee_name,
          is_on_site: e.is_on_site,
        });
      }
    });
    return Array.from(byId.values());
  }, [scheduledCandidates, companyEmployees]);

  const candidates = isClockOut ? onSiteCandidates : clockInCandidates;

  const [timestampDate, setTimestampDate] = useState<Date | null>(new Date());

  const form = useForm<FormValues>({
    validate: yupResolver(ClockOverrideSchema),
    initialValues: {
      clock_type: TAB_KEYS.CLOCK_IN,
      timestamp: formatTimeHHMM(new Date()),
      reason: "",
      no_show_reason: "",
      no_show_reason_other: "",
    },
  });

  const handleTimestampChange = useCallback(
    (value: Date) => {
      setTimestampDate(value);
      form.setFieldValue("timestamp", formatTimeHHMM(value));
    },
    [form]
  );

  const { mutate: checkShiftClock, isLoading: isChecking } =
    useCheckShiftClockApiService();
  const { mutate: createBulkShiftClock, isLoading: isCreating } =
    useCreateBulkShiftClockApiService();

  const handleTabChange = (index: number, tabKey?: string): void => {
    setActiveTabIndex(index);
    setSelectedEmployeeIds([]);
    setEmployeeSearch("");
    if (tabKey) {
      form.setFieldValue("clock_type", tabKey);
    }
  };

  // On clock-out the candidates span whatever shift their clock-in landed on,
  // so a single batch shift_config can't be right for everyone — send null and
  // let the backend pair the clock-out to the open clock-in by location + time.
  const shiftConfigForSubmit = isClockOut ? null : selectedShiftId || null;

  const executeBulkCreate = (values: FormValues): void => {
    createBulkShiftClock(
      {
        employee_ids: selectedEmployeeIds,
        shift_location: locationId,
        shift_config: shiftConfigForSubmit,
        clock_type: values.clock_type as ClockType,
        timestamp: buildIsoTimestamp(values.timestamp),
        reason: values.reason,
      },
      {
        onSuccess: () => {
          showNotification({
            title: "Success",
            message: "Clock records have been created successfully.",
            color: "green",
          });
          onSuccess();
        },
        onError: () => {
          showNotification({
            title: "Error",
            message: "Failed to create clock records. Please try again.",
            color: "red",
          });
        },
      }
    );
  };

  const handleSubmit = (values: FormValues): void => {
    if (selectedEmployeeIds.length === 0) {
      showNotification({
        title: "No employees selected",
        message: "Please select at least one employee.",
        color: "orange",
      });
      return;
    }

    checkShiftClock(
      {
        employee_ids: selectedEmployeeIds,
        shift_location: locationId,
        shift_config: shiftConfigForSubmit,
        clock_type: values.clock_type as ClockType,
        timestamp: buildIsoTimestamp(values.timestamp),
        reason: values.reason,
      },
      {
        onSuccess: (response) => {
          if (response.warnings && response.warnings.length > 0) {
            setWarnings(response.warnings);
            setConfirmModalOpen(true);
          } else {
            executeBulkCreate(values);
          }
        },
        onError: () => {
          showNotification({
            title: "Validation failed",
            message: "Could not validate the clock records. Please try again.",
            color: "red",
          });
        },
      }
    );
  };

  const handleConfirmWarnings = (): void => {
    setConfirmModalOpen(false);
    executeBulkCreate(form.values);
  };

  const handleCancelWarnings = (): void => {
    setConfirmModalOpen(false);
    setWarnings([]);
  };

  const isLoading = isChecking || isCreating;

  return (
    <>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing="lg">
          <Tabs active={activeTabIndex} onTabChange={handleTabChange}>
            <Tabs.Tab label="Clock In" tabKey={TAB_KEYS.CLOCK_IN} />
            <Tabs.Tab label="Clock Out" tabKey={TAB_KEYS.CLOCK_OUT} />
          </Tabs>

          {rosters.length > 0 && (
            <Select
              label="Roster"
              data={rosters.map((r) => ({ value: r.id, label: r.name }))}
              value={locationId}
              onChange={(val) => {
                if (val && val !== locationId) onRosterChange(val);
              }}
              size="sm"
            />
          )}

          {/* Shift drives clock-in attribution only; clock-out pairs by time. */}
          {!isClockOut && shiftConfigs.length > 1 && (
            <Select
              label="Shift"
              data={shiftConfigs.map((c) => ({ value: c.id, label: c.name }))}
              value={selectedShiftId}
              onChange={(val) => {
                setSelectedShiftId(val || "");
                setSelectedEmployeeIds([]);
              }}
              size="sm"
            />
          )}

          <EmployeeCheckboxList
            employees={candidates}
            selectedIds={selectedEmployeeIds}
            onChange={setSelectedEmployeeIds}
            search={employeeSearch}
            onSearchChange={setEmployeeSearch}
            loading={!isClockOut && isSearchingCompany}
            emptyLabel={
              isClockOut
                ? "No employees are currently on site at this roster."
                : "No employees found. Search to add anyone in the company."
            }
          />

          <OverrideForm
            timestamp={timestampDate}
            onTimestampChange={handleTimestampChange}
            reason={form.values.reason}
            onReasonChange={(value) => form.setFieldValue("reason", value)}
            errors={{
              timestamp: form.errors.timestamp as string | undefined,
              reason: form.errors.reason as string | undefined,
            }}
          />

          <Group position="right" spacing="sm">
            <Button variant="default" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" loading={isLoading}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      </form>

      <Modal
        opened={confirmModalOpen}
        onClose={handleCancelWarnings}
        title="Confirm with warnings"
        size={isPhone ? "100%" : "md"}
        closeOnClickOutside={false}
        styles={
          isPhone
            ? {
                modal: {
                  height: "100vh",
                  maxHeight: "100vh",
                  borderRadius: 0,
                },
              }
            : undefined
        }
      >
        <Stack spacing="md">
          <Text size="sm">
            The following warnings were found. Do you want to proceed?
          </Text>

          <Stack spacing="xs">
            {warnings.map((warning) => (
              <Text key={`${warning.employee_id}-${warning.message}`} size="sm">
                <Text component="span" weight={600}>
                  {warning.employee_name}
                </Text>
                {": "}
                {warning.message}
              </Text>
            ))}
          </Stack>

          <Group position="right" spacing="sm">
            <Button variant="default" onClick={handleCancelWarnings}>
              Cancel
            </Button>
            <Button onClick={handleConfirmWarnings} loading={isCreating}>
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default BatchClockForm;
